import React from 'react';
import { Box } from '@mui/material';
import {
  signalBorders,
  signalColors,
  signalDawLogos,
  signalFonts,
  signalRadii,
  signalShadows,
  signalStatus,
  signalSurfaces,
  signalText,
} from '@/theme/foundations/tokens';
import {
  DawTile,
  InitialsAvatar,
  Mono,
  ScreenFrame,
  SectionLabel,
  SignalButton,
  StatusPill,
  Waveform,
} from '@/common/components/signal';
import { playerWave, versions } from '../fixtures';

/** One tab in the header's Files / Versions / Sync / People row. */
const Tab: React.FC<{ label: string; active?: boolean }> = ({
  label,
  active = false,
}) => (
  <Box
    sx={{
      padding: '0 0 12px',
      borderBottom: `2px solid ${active ? signalColors.accent : 'transparent'}`,
      color: active ? signalText.primary : signalText.muted,
      fontWeight: 600,
      fontSize: '13px',
      cursor: 'pointer',
      '&:hover': active ? undefined : { color: signalText.primary },
    }}
  >
    {label}
  </Box>
);

type TreeRow = {
  name: string;
  size: string;
  /** 0 = project root, 1 = folder, 2 = leaf inside a folder. */
  depth: 0 | 1 | 2;
  /** Disclosure triangle; omitted for leaves. */
  chevron?: '▾' | '▸';
  icon: string;
  iconColor: string;
  /** Bold + full-contrast: the root and the selected file. */
  emphasis?: boolean;
  /** Accent wash on the row currently playing. */
  selected?: boolean;
  badge?: { label: string; status: 'syncing' | 'behind' };
};

/**
 * The file tree is drawn in the design's markup rather than bound to data, so
 * it lives here as a flat list — the indentation is a property of the row, not
 * a nested structure, which is all a static mock needs.
 */
const tree: TreeRow[] = [
  {
    name: 'Nightdrive Project',
    size: '1.84 GB',
    depth: 0,
    chevron: '▾',
    icon: '▣',
    iconColor: signalColors.accentBright,
    emphasis: true,
  },
  {
    name: 'Samples',
    size: '1.21 GB · 286',
    depth: 1,
    chevron: '▾',
    icon: '▣',
    iconColor: signalColors.cyan,
  },
  {
    name: 'Imported',
    size: '874 MB',
    depth: 2,
    icon: '▤',
    iconColor: signalColors.cyan,
  },
  {
    name: 'Processed',
    size: '361 MB',
    depth: 2,
    icon: '▤',
    iconColor: signalColors.cyan,
  },
  {
    name: 'Renders',
    size: '302 MB · 9',
    depth: 1,
    chevron: '▾',
    icon: '▣',
    iconColor: signalColors.cyan,
  },
  {
    name: 'rough master v14.wav',
    size: '62.4 MB',
    depth: 2,
    icon: '♪',
    iconColor: signalColors.accent,
    emphasis: true,
    selected: true,
    badge: { label: 'PLAYING', status: 'syncing' },
  },
  {
    name: 'rough master v13.wav',
    size: '61.8 MB',
    depth: 2,
    icon: '♪',
    iconColor: signalColors.cyan,
  },
  {
    name: 'Ableton Project Info',
    size: '4.1 MB · 12',
    depth: 1,
    chevron: '▸',
    icon: '▣',
    iconColor: signalColors.cyan,
  },
  {
    name: 'Nightdrive.als',
    size: '18.4 MB',
    depth: 1,
    icon: '▤',
    iconColor: signalColors.accentBright,
    badge: { label: 'LOCAL NEWER', status: 'behind' },
  },
];

const INDENT: Record<TreeRow['depth'], string> = {
  0: '7px 10px',
  1: '7px 10px 7px 30px',
  2: '7px 10px 7px 52px',
};

const FileRow: React.FC<{ row: TreeRow }> = ({ row }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '9px',
      padding: INDENT[row.depth],
      borderRadius: `${signalRadii.sm}px`,
      background: row.selected ? 'rgba(255,106,31,0.12)' : 'transparent',
      color: row.emphasis
        ? signalText.primary
        : row.depth === 2
          ? signalText.secondary
          : signalText.bright,
      fontWeight: row.emphasis ? 600 : 400,
      cursor: 'pointer',
      '&:hover':
        row.emphasis || row.selected
          ? undefined
          : { background: signalSurfaces.fillStrong },
    }}
  >
    {row.chevron && (
      <Box component="span" sx={{ color: signalText.muted, fontSize: '10px' }}>
        {row.chevron}
      </Box>
    )}
    <Box component="span" sx={{ color: row.iconColor }}>
      {row.icon}
    </Box>
    {row.name}
    {row.badge && (
      <StatusPill
        status={row.badge.status}
        size={9}
        borderless
        sx={{ marginLeft: '8px' }}
      >
        {row.badge.label}
      </StatusPill>
    )}
    <Mono size={11} sx={{ marginLeft: 'auto' }}>
      {row.size}
    </Mono>
  </Box>
);

/** One line in the SYNC panel: a dot, a path, and its state. */
const SyncRow: React.FC<{ label: string; state: string; color: string }> = ({
  label,
  state,
  color,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: '12px',
      color: signalText.secondary,
    }}
  >
    <Box
      component="span"
      sx={{ width: 6, height: 6, borderRadius: '999px', background: color }}
    />
    {label}
    <Mono size={10.5} color={color} sx={{ marginLeft: 'auto' }}>
      {state}
    </Mono>
  </Box>
);

const people = [
  { initials: 'MK', name: 'Mara Kade', role: 'OWNER', owner: true },
  { initials: 'JD', name: 'Jonah Dey', role: 'COLLABORATOR', owner: false },
  { initials: 'AR', name: 'Ada Rein', role: 'VIEWER', owner: false },
] as const;

const PERSON_TONE: Record<string, { bg: string; fg: string }> = {
  MK: { bg: signalColors.accent, fg: signalColors.onAccent },
  JD: { bg: signalColors.cyan, fg: signalColors.onCyan },
  AR: { bg: signalColors.green, fg: signalColors.onGreen },
};

/**
 * 02 — Project detail.
 *
 * Two columns: what the project *is* on the left (the render you are hearing,
 * then every file behind it), and what has *happened to it* on the right
 * (version history, sync divergence, who can touch it). The playhead, the
 * highlighted tree row and the "1 file ahead" panel all describe the same
 * moment, so the screen reads as one state rather than three widgets.
 */
export const Screen02ProjectDetail: React.FC = () => (
  <ScreenFrame index="02" title="Project detail — Files, Versions, Sync">
    <Box
      sx={{
        padding: '22px 26px 0',
        display: 'flex',
        gap: '16px',
        alignItems: 'flex-start',
        borderBottom: `1px solid ${signalBorders.divider}`,
      }}
    >
      <DawTile
        src={signalDawLogos.Ableton}
        label="Ableton Live"
        size={52}
        radius={12}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Box
            component="h3"
            sx={{
              margin: 0,
              fontFamily: signalFonts.display,
              fontWeight: 800,
              fontSize: '30px',
              letterSpacing: '-0.03em',
              lineHeight: 1,
            }}
          >
            Nightdrive
          </Box>
          <StatusPill size={10}>v14</StatusPill>
          <StatusPill status="synced" dot size={10}>
            SYNCED
          </StatusPill>
        </Box>

        <Mono size={11} sx={{ display: 'block', marginTop: '7px' }}>
          ABLETON LIVE 12 · 128 BPM · Fm · 1.84 GB · 412 FILES
        </Mono>

        <Box sx={{ display: 'flex', gap: '22px', marginTop: '16px' }}>
          <Tab label="Files" active />
          <Tab label="Versions" />
          <Tab label="Sync" />
          <Tab label="People" />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <SignalButton variant="secondary">Share</SignalButton>
        <SignalButton>Open in Ableton</SignalButton>
      </Box>
    </Box>

    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 380px' }}>
      <Box
        sx={{
          padding: '20px 24px 26px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          borderRight: `1px solid ${signalBorders.divider}`,
        }}
      >
        <Box
          sx={{
            border: `1px solid ${signalBorders.medium}`,
            borderRadius: `${signalRadii['2xl']}px`,
            background: signalSurfaces.cardGradientSoft,
            padding: '18px 18px 16px',
            boxShadow: signalShadows.panel,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              marginBottom: '14px',
            }}
          >
            <Box
              component="button"
              type="button"
              aria-label="Pause"
              sx={{
                width: 44,
                height: 44,
                flex: 'none',
                borderRadius: '999px',
                border: 0,
                cursor: 'pointer',
                background: signalColors.accent,
                color: signalColors.onAccent,
                fontSize: '15px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: signalShadows.accentLg,
                '&:hover': { background: signalColors.accentHover },
              }}
            >
              ❚❚
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ fontWeight: 600, fontSize: '14px' }}>
                Nightdrive — rough master v14.wav
              </Box>
              <Mono size={11} sx={{ display: 'block', marginTop: '3px' }}>
                RENDERED 4 MIN AGO · 48 kHz / 24-bit · 62.4 MB
              </Mono>
            </Box>

            <Mono size={15} weight={600} color={signalText.primary}>
              01:42.06
              <Box
                component="span"
                sx={{ color: signalText.faint, fontSize: '12px' }}
              >
                {' / 03:58.20'}
              </Box>
            </Mono>
          </Box>

          {/* The playhead sits at 43%, the same fraction the waveform was
              generated with, so the played/unplayed split lines up with it. */}
          <Box sx={{ position: 'relative', cursor: 'pointer' }}>
            <Waveform bars={playerWave} height={86} />
            <Box
              sx={{
                position: 'absolute',
                left: '43%',
                top: 0,
                bottom: 0,
                width: '2px',
                background: signalText.primary,
                boxShadow: '0 0 12px rgba(255,255,255,0.6)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                left: '43%',
                top: '-6px',
                transform: 'translateX(-50%)',
                width: 10,
                height: 10,
                borderRadius: '999px',
                background: signalText.primary,
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '10px',
            }}
          >
            {['00:00', '01:00', '02:00', '03:00', '03:58'].map((t) => (
              <Mono key={t} size={10} color={signalText.faint}>
                {t}
              </Mono>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            border: `1px solid ${signalBorders.default}`,
            borderRadius: `${signalRadii['2xl']}px`,
            background: signalSurfaces.fillSubtle,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              borderBottom: `1px solid ${signalBorders.divider}`,
            }}
          >
            <SectionLabel size={11}>FILE TREE · 412 FILES</SectionLabel>
            <Mono size={11}>1.84 GB</Mono>
          </Box>
          <Box
            sx={{
              padding: '8px 10px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: '1px',
              fontSize: '13px',
            }}
          >
            {tree.map((row) => (
              <FileRow key={row.name} row={row} />
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          padding: '20px 22px 26px',
          display: 'flex',
          flexDirection: 'column',
          gap: '22px',
          background: signalSurfaces.fillFaint,
        }}
      >
        <Box>
          <SectionLabel
            size={11}
            sx={{ display: 'block', marginBottom: '14px' }}
          >
            VERSION HISTORY
          </SectionLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {versions.map((v) => (
              <Box
                key={v.tag}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '26px 1fr',
                  gap: '10px',
                }}
              >
                {/* Dot + connector: the rail is drawn per row so the last
                    entry's line simply ends with the list. */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '2px',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 11,
                      height: 11,
                      flex: 'none',
                      borderRadius: '999px',
                      background: v.dot,
                      boxShadow: `0 0 0 4px ${v.ring}`,
                      marginTop: '4px',
                    }}
                  />
                  <Box
                    component="span"
                    sx={{
                      flex: 1,
                      width: '1px',
                      background: signalBorders.medium,
                    }}
                  />
                </Box>

                <Box sx={{ paddingBottom: '18px' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: '8px',
                    }}
                  >
                    <Mono size={12} weight={600} color={v.dot}>
                      {v.tag}
                    </Mono>
                    <Box sx={{ fontWeight: 600, fontSize: '13px' }}>
                      {v.title}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      fontSize: '12px',
                      color: signalText.secondary,
                      marginTop: '4px',
                      lineHeight: 1.5,
                    }}
                  >
                    {v.note}
                  </Box>
                  <Mono size={10.5} sx={{ display: 'block', marginTop: '6px' }}>
                    {v.meta}
                  </Mono>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            border: `1px solid ${signalBorders.default}`,
            borderRadius: `${signalRadii['2xl']}px`,
            background: signalSurfaces.fillWeak,
            padding: '16px',
          }}
        >
          <SectionLabel
            size={11}
            sx={{ display: 'block', marginBottom: '12px' }}
          >
            SYNC
          </SectionLabel>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px',
            }}
          >
            <Box sx={{ fontSize: '13px', fontWeight: 600 }}>
              1 file ahead locally
            </Box>
            <SignalButton size="sm" glow={false}>
              Push v15
            </SignalButton>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
            <SyncRow
              label="Nightdrive.als"
              state="LOCAL NEWER"
              color={signalStatus.behind.fg}
            />
            <SyncRow
              label="Renders/ (9)"
              state="UP TO DATE"
              color={signalStatus.synced.fg}
            />
            <SyncRow
              label="Samples/ (286)"
              state="UP TO DATE"
              color={signalStatus.synced.fg}
            />
            <Box
              sx={{
                marginTop: '6px',
                paddingTop: '12px',
                borderTop: `1px solid ${signalBorders.divider}`,
              }}
            >
              <Mono size={10.5} sx={{ display: 'block' }}>
                LOCAL PATH
              </Mono>
              <Mono size={10.5} color={signalText.secondary}>
                ~/Music/Ableton/Nightdrive Project
              </Mono>
            </Box>
          </Box>
        </Box>

        <Box>
          <SectionLabel
            size={11}
            sx={{ display: 'block', marginBottom: '12px' }}
          >
            PEOPLE
          </SectionLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {people.map((person) => (
              <Box
                key={person.initials}
                sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}
              >
                <InitialsAvatar
                  initials={person.initials}
                  bg={PERSON_TONE[person.initials].bg}
                  fg={PERSON_TONE[person.initials].fg}
                  size={26}
                />
                <Box sx={{ fontSize: '13px' }}>{person.name}</Box>
                <StatusPill
                  status={person.owner ? 'syncing' : 'neutral'}
                  sx={{ marginLeft: 'auto', padding: '3px 8px' }}
                >
                  {person.role}
                </StatusPill>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  </ScreenFrame>
);

export default Screen02ProjectDetail;
